/* 2D-Ansicht (Draufsicht, Vorderansicht, Seitenansicht) auf Canvas2D mit allen Werkzeug-Interaktionen. */
import Cmd from './model/cmds.ts';
import Core from './core.ts';
import Doors from './model/doors.ts';
import EntDefs from './model/entdefs.ts';
import { boxOf } from './model/entvisual.ts';
import Gens from './model/gens.ts';
import I18n from './i18n.ts';
import { wheelAction, zoomFactor } from './model/wheel.ts';

const IS_MAC = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || navigator.userAgent || '');
const View2D = (function (Core, Cmd, EntDefs, Gens, Doors) {
  'use strict';
  const _ = I18n.t;

  const AX = {
    top: { h: 0, v: 1, d: 2, name: 'Draufsicht', hl: 'X', vl: 'Y' },
    front: { h: 0, v: 2, d: 1, name: 'Vorderansicht', hl: 'X', vl: 'Z' },
    side: { h: 1, v: 2, d: 0, name: 'Seitenansicht', hl: 'Y', vl: 'Z' }
  };
  const HANDLE_CURSORS = ['nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize'];

  const infoCache = new WeakMap();
  function info(b) {
    let i = infoCache.get(b);
    if (i) return i;
    const edges = [];
    b.faces.forEach((f) => f.vi.forEach((a, k) => { const c = f.vi[(k + 1) % f.vi.length]; if (a < c) edges.push([a, c]); }));
    i = { edges: edges, flat: Int32Array.from([].concat.apply([], edges)), bb: Core.bbox(b) };
    infoCache.set(b, i);
    return i;
  }
  function hull2(pts) {
    const p = pts.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    if (p.length < 3) return p;
    const cr = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const lo = [], up = [];
    p.forEach((q) => { while (lo.length >= 2 && cr(lo[lo.length - 2], lo[lo.length - 1], q) <= 0) lo.pop(); lo.push(q); });
    for (let i = p.length - 1; i >= 0; i--) { const q = p[i]; while (up.length >= 2 && cr(up[up.length - 2], up[up.length - 1], q) <= 0) up.pop(); up.push(q); }
    lo.pop(); up.pop();
    return lo.concat(up);
  }
  function inPoly(poly, x, y) {
    let c = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const a = poly[i], b = poly[j];
      if ((a[1] > y) !== (b[1] > y) && x < (b[0] - a[0]) * (y - a[1]) / (b[1] - a[1]) + a[0]) c = !c;
    }
    return c;
  }
  const polyArea = (poly) => { let s = 0; for (let i = 0; i < poly.length; i++) { const a = poly[i], b = poly[(i + 1) % poly.length]; s += a[0] * b[1] - b[0] * a[1]; } return Math.abs(s) / 2; };

  class View2D {
    constructor(app, canvas, key) {
      this.app = app; this.canvas = canvas; this.key = key; this.ax = AX[key];
      this.ctx = canvas.getContext('2d');
      this.w = 100; this.h = 100; this.dpr = 1;
      this.cx = 0; this.cy = 0; this.scale = 0.6;
      this.drag = null; this.space = false; this.mouse = null; this.pending = false; this.touches = new Map(); this.gesture = null;
      this.hullCache = new WeakMap();
      this.bind();
    }

    // ---------------------------------------------------------- Koordinaten
    sx(wh) { return this.w / 2 + (wh - this.cx) * this.scale; }
    sy(wv) { return this.h / 2 - (wv - this.cy) * this.scale; }
    toWorld(px, py) { return [(px - this.w / 2) / this.scale + this.cx, -(py - this.h / 2) / this.scale + this.cy]; }
    pos(e) { const r = this.canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; }
    snap(x) { const g = this.app.snapOn ? this.app.grid : 1; return Math.round(x / g) * g; }
    project(v) { return [v[this.ax.h], v[this.ax.v]]; }

    resize() {
      const r = this.canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(10, Math.round(r.width)), h = Math.max(10, Math.round(r.height));
      if (w !== this.w || h !== this.h || dpr !== this.dpr || this.canvas.width !== Math.round(w * dpr)) {
        this.w = w; this.h = h; this.dpr = dpr;
        this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr);
      }
      this.invalidate();
    }
    /** Weltgrenzen des gerade aufgezogenen Brushes (Tiefe aus den Optionen) oder null, wenn er noch zu klein ist */
    boxBounds(d) {
      const ax = this.ax, w = Math.abs(d.b[0] - d.a[0]), h = Math.abs(d.b[1] - d.a[1]);
      if (!(w >= 1 && h >= 1 && Math.hypot((d.b[0] - d.a[0]) * this.scale, (d.b[1] - d.a[1]) * this.scale) >= 4)) return null;
      const mn = [0, 0, 0], mx = [0, 0, 0];
      mn[ax.h] = Math.min(d.a[0], d.b[0]); mx[ax.h] = Math.max(d.a[0], d.b[0]);
      mn[ax.v] = Math.min(d.a[1], d.b[1]); mx[ax.v] = Math.max(d.a[1], d.b[1]);
      const dr = this.app.depth[ax.d]; mn[ax.d] = dr[0]; mx[ax.d] = dr[1];
      return { mn: mn, mx: mx };
    }
    fit(box, pad) {
      if (!box) return;
      const ax = this.ax, w = Math.max(box.max[ax.h] - box.min[ax.h], 64), h = Math.max(box.max[ax.v] - box.min[ax.v], 64);
      this.cx = (box.min[ax.h] + box.max[ax.h]) / 2; this.cy = (box.min[ax.v] + box.max[ax.v]) / 2;
      this.scale = Math.max(0.02, Math.min(8, Math.min(this.w / (w * (pad || 1.15)), this.h / (h * (pad || 1.15)))));
      this.invalidate();
    }
    invalidate() {
      if (this.pending) return;
      this.pending = true;
      requestAnimationFrame(() => { this.pending = false; this.draw(); });
    }

    // ---------------------------------------------------------- Zeichnen
    draw() {
      const app = this.app, ctx = this.ctx, C = app.colors(), ax = this.ax;
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.fillStyle = C.bg; ctx.fillRect(0, 0, this.w, this.h);
      this.drawGrid(ctx, C);
      const doc = app.viewDoc(), sel = app.sel;
      const x0 = this.toWorld(0, this.h)[0], x1 = this.toWorld(this.w, 0)[0];
      const y0 = this.toWorld(0, this.h)[1], y1 = this.toWorld(this.w, 0)[1];
      const visible = (bb) => bb.max[ax.h] >= x0 && bb.min[ax.h] <= x1 && bb.max[ax.v] >= y0 && bb.min[ax.v] <= y1;
      // Brushes
      const plain = [], entb = new Map(), selected = [];
      doc.ents.forEach((e, ei) => e.brushes.forEach((b) => {
        const inf = info(b);
        if (!visible(inf.bb)) return;
        if (sel.b.has(b.id)) selected.push(b);
        else if (ei === 0) plain.push(b);
        else { const c = EntDefs.colorFor(e.kv.classname); if (!entb.has(c)) entb.set(c, []); entb.get(c).push(b); }
      }));
      this.drawStatic(ctx, C, plain, entb);
      // Auswahl: Füllung + Kanten
      if (selected.length) {
        ctx.fillStyle = C.selFill;
        selected.forEach((b) => { const hl = this.hull(b); if (hl.pts.length > 2) { ctx.beginPath(); hl.pts.forEach((p, i) => { const X = this.sx(p[0]), Y = this.sy(p[1]); if (i) ctx.lineTo(X, Y); else ctx.moveTo(X, Y); }); ctx.closePath(); ctx.fill(); } });
        ctx.strokeStyle = C.sel; ctx.lineWidth = 1.5; this.strokeBrushes(ctx, selected); ctx.lineWidth = 1;
      }
      this.drawBsp(ctx);
      this.drawEntities(ctx, doc, sel, C);
      this.drawDoors(ctx, doc, sel, C);
      this.drawLeak(ctx);
      this.drawMarks(ctx);
      this.drawOverlay(ctx, doc, sel, C);
      // Rahmen
      if (app.activeView === this) { ctx.strokeStyle = C.sel; ctx.lineWidth = 1; ctx.strokeRect(0.5, 0.5, this.w - 1, this.h - 1); }
    }

    drawGrid(ctx, C) {
      const app = this.app;
      let step = Math.max(1, app.grid);
      while (step * this.scale < 9) step *= 2;
      const x0 = this.toWorld(0, 0)[0], x1 = this.toWorld(this.w, 0)[0], y1 = this.toWorld(0, 0)[1], y0 = this.toWorld(0, this.h)[1];
      const major = step * 8;
      const lines = (a0, a1, horizontal) => {
        const s = Math.floor(a0 / step) * step;
        for (let a = s; a <= a1; a += step) {
          const isMajor = Math.abs(Math.round(a / major) * major - a) < 1e-6, isAxis = a === 0;
          ctx.strokeStyle = isAxis ? C.axis : isMajor ? C.gridMajor : C.grid;
          ctx.lineWidth = isAxis ? 1.5 : 1;
          ctx.beginPath();
          if (horizontal) { const Y = Math.round(this.sy(a)) + 0.5; ctx.moveTo(0, Y); ctx.lineTo(this.w, Y); }
          else { const X = Math.round(this.sx(a)) + 0.5; ctx.moveTo(X, 0); ctx.lineTo(X, this.h); }
          ctx.stroke();
        }
      };
      lines(x0, x1, false); lines(y0, y1, true);
      // Beschriftung
      ctx.font = '10px "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace';
      ctx.fillStyle = C.dim; ctx.textBaseline = 'top';
      let ls = major; while (ls * this.scale < 64) ls *= 2;
      for (let a = Math.ceil(x0 / ls) * ls; a <= x1; a += ls) ctx.fillText(String(a), this.sx(a) + 3, 3);
      for (let a = Math.ceil(y0 / ls) * ls; a <= y1; a += ls) ctx.fillText(String(a), 3, this.sy(a) + 3);
    }

    hull(b) {
      let r = this.hullCache.get(b);
      if (!r) {
        const pts = hull2(b.verts.map((v) => this.project(v)));
        r = { pts: pts, area: polyArea(pts) };
        this.hullCache.set(b, r);
      }
      return r;
    }
    strokeBrushes(ctx, list) {
      const H = this.ax.h, V = this.ax.v, k = this.scale, ox = this.w / 2 - this.cx * k, oy = this.h / 2 + this.cy * k; // = sx/sy, ohne Methodenaufruf je Punkt
      ctx.beginPath();
      for (let n = 0; n < list.length; n++) {
        const v = list[n].verts, f = info(list[n]).flat;
        for (let i = 0; i < f.length; i += 2) {
          const a = v[f[i]], c = v[f[i + 1]];
          ctx.moveTo(ox + a[H] * k, oy - a[V] * k); ctx.lineTo(ox + c[H] * k, oy - c[V] * k);
        }
      }
      ctx.stroke();
    }
    /** Unveränderte Brushes (Welt und Entities) als Bild zwischenspeichern: bleiben Ansicht und Brushes gleich (Auswahl ziehen, Hover, Overlays),
     *  wird nur noch das Bild kopiert statt Zehntausende Kanten neu zu zeichnen. Beim Verschieben der Ansicht gibt es keinen Umweg über das Bild. */
    drawStatic(ctx, C, plain, entb) {
      const lists = [plain], cols = [C.wire];
      entb.forEach((l, c) => { lists.push(l); cols.push(c); });
      const key = [this.cx, this.cy, this.scale, this.w, this.h, this.dpr, this.ax.h, this.ax.v].join(',') + '|' + cols.join(',');
      const L = this._layer || (this._layer = { key: '', lists: null, canvas: null });
      const same = L.lists && L.key === key && L.lists.length === lists.length && lists.every((l, i) => l.length === L.lists[i].length && l.every((b, j) => b === L.lists[i][j]));
      const draw = (g) => { g.lineWidth = 1; lists.forEach((l, i) => { g.strokeStyle = cols[i]; this.strokeBrushes(g, l); }); };
      if (!same && L.key !== key) { L.key = key; L.lists = null; draw(ctx); return; } // Ansicht ändert sich gerade: direkt zeichnen
      if (!same) {
        if (!L.canvas) L.canvas = document.createElement('canvas');
        L.canvas.width = this.canvas.width; L.canvas.height = this.canvas.height;
        const g = L.canvas.getContext('2d'); g.setTransform(this.dpr, 0, 0, this.dpr, 0, 0); draw(g);
        L.lists = lists;
      }
      ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.drawImage(L.canvas, 0, 0); ctx.restore();
    }

    drawBsp(ctx) {
      const B = this.app.bsp; if (!B) return;
      const L = B.scene.lines, ax = this.ax;
      ctx.save(); ctx.strokeStyle = 'rgba(120,135,160,0.75)'; ctx.lineWidth = 1; ctx.beginPath();
      for (let i = 0; i < L.length; i += 6) {
        const x0 = this.sx(L[i + ax.h]), y0 = this.sy(L[i + ax.v]), x1 = this.sx(L[i + 3 + ax.h]), y1 = this.sy(L[i + 3 + ax.v]);
        if ((x0 < 0 && x1 < 0) || (y0 < 0 && y1 < 0) || (x0 > this.w && x1 > this.w) || (y0 > this.h && y1 > this.h)) continue;
        ctx.moveTo(x0, y0); ctx.lineTo(x1, y1);
      }
      ctx.stroke(); ctx.restore();
    }

    drawEntities(ctx, doc, sel, C) {
      const ax = this.ax;
      ctx.font = '11px "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace';
      ctx.textBaseline = 'middle';
      if (!this.app.showEnts) return;   // Entities ausgeblendet
      doc.ents.forEach((e, i) => {
        if (!Cmd.isPointEnt(e, i)) return;
        const o = Core.parseVec(e.kv.origin);
        if (!o) return;
        const X = this.sx(o[ax.h]), Y = this.sy(o[ax.v]);
        if (X < -40 || Y < -20 || X > this.w + 40 || Y > this.h + 20) return;
        const col = EntDefs.colorFor(e.kv.classname), s = Math.max(4, Math.min(9, 8 * this.scale * 2)), isSel = sel.e.has(e.id);
        ctx.fillStyle = col; ctx.strokeStyle = col;
        // Box der Klasse (size() aus der FGD): als Rechteck um den Ursprung, wenn sie in dieser Ansicht groß genug erscheint
        let box = null;
        const bb = boxOf(e.kv.classname);
        if (bb) {
          const x0 = this.sx(o[ax.h] + bb.min[ax.h]), x1 = this.sx(o[ax.h] + bb.max[ax.h]), y0 = this.sy(o[ax.v] + bb.min[ax.v]), y1 = this.sy(o[ax.v] + bb.max[ax.v]);
          if (Math.abs(x1 - x0) >= 6 || Math.abs(y1 - y0) >= 6) box = { x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.max(2, Math.abs(x1 - x0)), h: Math.max(2, Math.abs(y1 - y0)) };
        }
        const spr = this.app.spriteFor(e);
        if (spr && spr.canvas) { const k = parseFloat(e.kv.scale) || 1, sw = Math.max(6, Math.min(80, spr.w * k * this.scale)), shh = sw * spr.h / spr.w; ctx.globalAlpha = 0.9; ctx.drawImage(spr.canvas, X - sw / 2, Y - shh / 2, sw, shh); ctx.globalAlpha = 1; }
        const ik = EntDefs.iconKind(e.kv.classname);
        if (ik) {
          if (isSel || this.scale > 0.12) { const rad = EntDefs.soundRadius(e.kv); if (rad && (isSel || this.scale > 0.35)) { ctx.save(); ctx.globalAlpha = isSel ? 0.55 : 0.18; ctx.setLineDash([5, 5]); ctx.beginPath(); ctx.arc(X, Y, rad * this.scale, 0, 6.2832); ctx.stroke(); ctx.restore(); } }
          EntDefs.drawIcon(ctx, ik, X, Y, Math.max(16, s * 2.6), col, C.bg || 'rgba(0,0,0,0.35)');
        }
        else if (/^light/.test(e.kv.classname)) { ctx.beginPath(); ctx.arc(X, Y, s, 0, 6.2832); ctx.fill(); }
        else if (box) { ctx.globalAlpha = 0.28; ctx.fillRect(box.x, box.y, box.w, box.h); ctx.globalAlpha = 1; ctx.strokeRect(box.x, box.y, box.w, box.h); }
        else { ctx.fillRect(X - s, Y - s, s * 2, s * 2); }
        if (this.key === 'top') {
          let yaw = null;
          if (e.kv.angles) { const a = e.kv.angles.trim().split(/\s+/).map(Number); if (!isNaN(a[1])) yaw = a[1]; }
          if (yaw !== null && /^(info_player|monster_)/.test(e.kv.classname)) {
            const r = yaw * Math.PI / 180;
            ctx.beginPath(); ctx.moveTo(X, Y); ctx.lineTo(X + Math.cos(r) * (s + 14), Y - Math.sin(r) * (s + 14)); ctx.lineWidth = 2; ctx.stroke(); ctx.lineWidth = 1;
          }
        }
        if (isSel) { ctx.strokeStyle = C.sel; ctx.lineWidth = 2; if (box) ctx.strokeRect(box.x - 3, box.y - 3, box.w + 6, box.h + 6); else ctx.strokeRect(X - s - 3, Y - s - 3, s * 2 + 6, s * 2 + 6); ctx.lineWidth = 1; }
        if (isSel || this.scale > 0.9) { ctx.fillStyle = isSel ? C.sel : C.dim; ctx.fillText(e.kv.classname, X + s + 5, Y); }
      });
    }

    /** Türen: offener Zustand gestrichelt, Weg als Pfeil bzw. Drehbogen; dazu target-Verbindungen. */
    drawDoors(ctx, doc, sel, C) {
      const app = this.app, ax = this.ax, all = app.showAids;
      const P = (p) => [this.sx(p[ax.h]), this.sy(p[ax.v])];
      const mean = (pts) => { const s = [0, 0]; pts.forEach((q) => { s[0] += q[0]; s[1] += q[1]; }); return [s[0] / pts.length, s[1] / pts.length]; };
      const arrow = (a, b, col) => {
        const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy);
        if (L < 3) return;
        ctx.strokeStyle = col; ctx.fillStyle = col; ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
        const ux = dx / L, uy = dy / L;
        ctx.beginPath(); ctx.moveTo(b[0], b[1]); ctx.lineTo(b[0] - ux * 9 - uy * 4, b[1] - uy * 9 + ux * 4); ctx.lineTo(b[0] - ux * 9 + uy * 4, b[1] - uy * 9 - ux * 4); ctx.closePath(); ctx.fill();
      };
      ctx.lineWidth = 1.5; ctx.font = '11px "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace'; ctx.textBaseline = 'bottom'; ctx.textAlign = 'left';
      doc.ents.forEach((e) => {
        if (!Doors.active(e, sel, all)) return;
        const d = Doors.describe(e); if (!d) return;
        const col = '#ff9f1a', hl = hull2(d.c1.map((q) => [this.sx(q[ax.h]), this.sy(q[ax.v])]));
        if (hl.length < 3) return;
        ctx.setLineDash([5, 3]); ctx.strokeStyle = col; ctx.beginPath();
        hl.forEach((q, i) => (i ? ctx.lineTo(q[0], q[1]) : ctx.moveTo(q[0], q[1]))); ctx.closePath(); ctx.stroke();
        const c0 = mean(d.c0.map(P)), c1 = mean(d.c1.map(P));
        ctx.setLineDash([]);
        if (d.kind === 'slide') arrow(c0, c1, col);
        else {
          const pv = P(d.pivot);
          ctx.strokeStyle = col; ctx.beginPath(); ctx.moveTo(pv[0] - 6, pv[1]); ctx.lineTo(pv[0] + 6, pv[1]); ctx.moveTo(pv[0], pv[1] - 6); ctx.lineTo(pv[0], pv[1] + 6); ctx.stroke();
          if (d.axis === ax.d) {
            const w0 = [d.c0.reduce((s, q) => s + q[ax.h], 0) / 8 - d.pivot[ax.h], d.c0.reduce((s, q) => s + q[ax.v], 0) / 8 - d.pivot[ax.v]];
            const r = Math.hypot(w0[0], w0[1]) * this.scale, a0 = Math.atan2(w0[1], w0[0]), sweep = (d.axis === 1 ? -1 : 1) * d.deg * Math.PI / 180;
            if (r > 3) {
              ctx.beginPath(); ctx.arc(pv[0], pv[1], r, -a0, -(a0 + sweep), sweep > 0); ctx.stroke();
              const e1 = [pv[0] + r * Math.cos(-(a0 + sweep)), pv[1] + r * Math.sin(-(a0 + sweep))], t = sweep > 0 ? 1 : -1, ang = -(a0 + sweep);
              arrow([e1[0] + Math.sin(ang) * t * 8, e1[1] - Math.cos(ang) * t * 8], e1, col);
            }
          }
        }
        ctx.fillStyle = col;
        const lx = Math.min.apply(null, hl.map((q) => q[0])), ly = Math.min.apply(null, hl.map((q) => q[1]));
        ctx.fillText(d.label, lx, ly - 4);
      });
      ctx.setLineDash([4, 3]); ctx.lineWidth = 1;
      Doors.links(doc).forEach((l) => {
        if (!all && !Doors.active(l.from, sel, false) && !Doors.active(l.to, sel, false)) return;
        arrow(P(l.a), P(l.b), l.kill ? '#ff5273' : '#6fa8ff');
      });
      ctx.setLineDash([]); ctx.textAlign = 'left';
    }

    drawMarks(ctx) {
      const M = this.app.marks; if (!M || !M.length) return;
      const ax = this.ax, P = (p) => [this.sx(p[ax.h]), this.sy(p[ax.v])];
      ctx.save(); ctx.setLineDash([]); ctx.fillStyle = 'rgba(255,45,77,0.35)'; ctx.strokeStyle = '#ff2d4d'; ctx.lineWidth = 1.5;
      M.forEach((poly) => {
        ctx.beginPath(); poly.forEach((p, i) => { const q = P(p); if (i) ctx.lineTo(q[0], q[1]); else ctx.moveTo(q[0], q[1]); });
        ctx.closePath(); ctx.fill(); ctx.stroke();
      });
      ctx.restore();
    }

    drawLeak(ctx) {
      const L = this.app.leak; if (!L) return;
      const ax = this.ax, P = (p) => [this.sx(p[ax.h]), this.sy(p[ax.v])];
      ctx.save(); ctx.setLineDash([]); ctx.lineJoin = 'round';
      ctx.strokeStyle = '#ff2d4d'; ctx.lineWidth = 2.5; ctx.beginPath();
      L.pts.forEach((p, i) => { const q = P(p); if (i) ctx.lineTo(q[0], q[1]); else ctx.moveTo(q[0], q[1]); });
      ctx.stroke();
      const s = P(L.pts[0]), e = P(L.pts[L.pts.length - 1]);
      ctx.fillStyle = '#ff2d4d'; ctx.beginPath(); ctx.arc(s[0], s[1], 6, 0, 6.2832); ctx.fill();
      ctx.fillStyle = '#ffffff'; ctx.strokeStyle = '#ff2d4d'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(e[0], e[1], 5, 0, 6.2832); ctx.fill(); ctx.stroke();
      ctx.font = '11px "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace'; ctx.fillStyle = '#ff2d4d'; ctx.textBaseline = 'bottom'; ctx.textAlign = 'left';
      ctx.fillText(_('v2.leak_start'), s[0] + 9, s[1] - 4); ctx.fillText(_('v2.leak_end'), e[0] + 9, e[1] - 4);
      ctx.restore();
    }

    handles() {
      const app = this.app;
      if (app.tool !== 'select' || !app.sel.b.size) return null;
      const bb = app.selBBox();
      if (!bb) return null;
      const ax = this.ax;
      const l = this.sx(bb.min[ax.h]), r = this.sx(bb.max[ax.h]), t = this.sy(bb.max[ax.v]), b = this.sy(bb.min[ax.v]);
      const mx = (l + r) / 2, my = (t + b) / 2;
      return { rect: [l, t, r, b], pts: [[l, t], [mx, t], [r, t], [r, my], [r, b], [mx, b], [l, b], [l, my]] };
    }

    drawOverlay(ctx, doc, sel, C) {
      const app = this.app, ax = this.ax, d = this.drag;
      const hs = this.handles();
      if (hs) {
        const [l, t, r, b] = hs.rect;
        ctx.strokeStyle = C.sel; ctx.setLineDash([4, 3]); ctx.strokeRect(l - 0.5, t - 0.5, r - l + 1, b - t + 1); ctx.setLineDash([]);
        ctx.fillStyle = C.bg; ctx.strokeStyle = C.sel;
        hs.pts.forEach((p) => { ctx.fillRect(p[0] - 3.5, p[1] - 3.5, 7, 7); ctx.strokeRect(p[0] - 3.5, p[1] - 3.5, 7, 7); });
        const bb = app.selBBox();
        ctx.fillStyle = C.sel; ctx.font = '10px "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace'; ctx.textBaseline = 'bottom'; ctx.textAlign = 'center';
        ctx.fillText(String(Math.round((bb.max[ax.h] - bb.min[ax.h]) * 100) / 100), (l + r) / 2, t - 6);
        ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
        ctx.fillText(String(Math.round((bb.max[ax.v] - bb.min[ax.v]) * 100) / 100), r + 8, (t + b) / 2);
        ctx.textAlign = 'left';
      }
      if (app.tool === 'vertex') {
        const seen = new Set(), hov = this.hoverVertex;
        ctx.lineWidth = 1;
        Cmd.selBrushes(doc, sel).forEach((b) => b.verts.forEach((v) => {
          const X = this.sx(v[ax.h]), Y = this.sy(v[ax.v]), k = Math.round(X) + ',' + Math.round(Y);
          if (seen.has(k)) return; seen.add(k);
          const isH = hov && Math.abs(hov[ax.h] - v[ax.h]) < 0.01 && Math.abs(hov[ax.v] - v[ax.v]) < 0.01;
          const isS = app.vertSel && Cmd.vertInSel(app.vertSel, v);
          ctx.fillStyle = isH || isS ? C.sel : C.bg; ctx.strokeStyle = C.sel;
          ctx.fillRect(X - 4, Y - 4, 8, 8); ctx.strokeRect(X - 4, Y - 4, 8, 8);
          if (isS) { ctx.strokeStyle = C.dim; ctx.strokeRect(X - 6.5, Y - 6.5, 13, 13); ctx.strokeStyle = C.sel; }
        }));
      }
      if (d && d.type === 'marquee') {
        const a = d.a, b = d.b;
        ctx.strokeStyle = C.sel; ctx.fillStyle = C.selFill; ctx.setLineDash([4, 3]);
        ctx.fillRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(a.x - b.x), Math.abs(a.y - b.y));
        ctx.strokeRect(Math.min(a.x, b.x) + 0.5, Math.min(a.y, b.y) + 0.5, Math.abs(a.x - b.x), Math.abs(a.y - b.y)); ctx.setLineDash([]);
      }
      if (d && d.type === 'box') {
        const l = this.sx(Math.min(d.a[0], d.b[0])), r = this.sx(Math.max(d.a[0], d.b[0])), t = this.sy(Math.max(d.a[1], d.b[1])), bt = this.sy(Math.min(d.a[1], d.b[1]));
        ctx.strokeStyle = C.sel; ctx.fillStyle = C.selFill; ctx.setLineDash([5, 3]);
        ctx.fillRect(l, t, r - l, bt - t); ctx.strokeRect(l + 0.5, t + 0.5, r - l, bt - t); ctx.setLineDash([]);
        ctx.fillStyle = C.sel; ctx.font = '11px "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace'; ctx.textBaseline = 'bottom';
        ctx.fillText(Math.abs(d.b[0] - d.a[0]) + ' × ' + Math.abs(d.b[1] - d.a[1]), l, t - 5);
      }
      const cl = app.clipLine;
      if (cl && cl.view === this.key) {
        const ax2 = [this.sx(cl.a[0]), this.sy(cl.a[1])], bx = [this.sx(cl.b[0]), this.sy(cl.b[1])];
        const dx = bx[0] - ax2[0], dy = bx[1] - ax2[1], L = Math.hypot(dx, dy) || 1;
        ctx.strokeStyle = C.sel; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(ax2[0] - dx / L * 4000, ax2[1] - dy / L * 4000); ctx.lineTo(bx[0] + dx / L * 4000, bx[1] + dy / L * 4000); ctx.stroke();
        ctx.lineWidth = 1; ctx.fillStyle = C.sel;
        [ax2, bx].forEach((p) => ctx.fillRect(p[0] - 4, p[1] - 4, 8, 8));
        // Pfeil zur behaltenen Seite
        const s = app.clipKeep === 'back' ? -1 : 1, nx = dy / L * s, ny = -dx / L * s, mx = (ax2[0] + bx[0]) / 2, my = (ax2[1] + bx[1]) / 2;
        ctx.beginPath(); ctx.moveTo(mx, my); ctx.lineTo(mx + nx * 30, my + ny * 30); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(mx + nx * 30, my + ny * 30); ctx.lineTo(mx + nx * 22 - ny * 5, my + ny * 22 + nx * 5); ctx.lineTo(mx + nx * 22 + ny * 5, my + ny * 22 - nx * 5); ctx.closePath(); ctx.fill();
      }
    }

    // ---------------------------------------------------------- Treffer
    pickAll(px, py) {
      const app = this.app, doc = app.viewDoc(), w = this.toWorld(px, py), ax = this.ax, out = [];
      doc.ents.forEach((e, i) => {
        if (!app.showEnts || !Cmd.isPointEnt(e, i)) return;
        const o = Core.parseVec(e.kv.origin); if (!o) return;
        const dx = this.sx(o[ax.h]) - px, dy = this.sy(o[ax.v]) - py;
        if (Math.abs(dx) <= 9 && Math.abs(dy) <= 9) out.push({ type: 'e', id: e.id, area: -1 });
      });
      doc.ents.forEach((e) => e.brushes.forEach((b) => {
        const bb = info(b).bb;
        if (w[0] < bb.min[ax.h] || w[0] > bb.max[ax.h] || w[1] < bb.min[ax.v] || w[1] > bb.max[ax.v]) return;
        const hl = this.hull(b);
        if (hl.pts.length >= 3 && inPoly(hl.pts, w[0], w[1])) out.push({ type: 'b', id: b.id, area: hl.area });
      }));
      out.sort((a, b) => a.area - b.area);
      return out;
    }
    hitHandle(px, py) {
      const hs = this.handles();
      if (!hs) return -1;
      for (let i = 0; i < 8; i++) if (Math.abs(hs.pts[i][0] - px) <= 6 && Math.abs(hs.pts[i][1] - py) <= 6) return i;
      return -1;
    }
    hitVertex(px, py) {
      const app = this.app, ax = this.ax;
      let best = null, bd = 8;
      Cmd.selBrushes(app.viewDoc(), app.sel).forEach((b) => b.verts.forEach((v, i) => {
        const d = Math.max(Math.abs(this.sx(v[ax.h]) - px), Math.abs(this.sy(v[ax.v]) - py));
        if (d <= bd) { bd = d; best = { id: b.id, idx: i, pos: v }; }
      }));
      return best;
    }

    // ---------------------------------------------------------- Interaktion
    bind() {
      const c = this.canvas;
      // Maus: Rechtsklick ohne Ziehen öffnet das Menü (siehe up). Touch: langes Tippen löst contextmenu aus.
      c.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        if (this.lastType === 'touch' && !this.gesture) { this.drag = null; this.app.endPreview(); this.app.contextAt(this, e); }
      });
      c.addEventListener('pointerdown', (e) => { this.lastType = e.pointerType; this.down(e); });
      c.addEventListener('pointermove', (e) => this.move(e));
      c.addEventListener('pointerup', (e) => this.up(e));
      c.addEventListener('pointercancel', (e) => this.up(e, true));
      c.addEventListener('pointerleave', () => { this.mouse = null; this.hoverVertex = null; this.invalidate(); });
      c.addEventListener('wheel', (e) => {
        e.preventDefault();
        if (wheelAction(e, IS_MAC) === 'pan') { this.cx += e.deltaX / this.scale; this.cy -= e.deltaY / this.scale; this.invalidate(); return; }
        const p = this.pos(e), before = this.toWorld(p.x, p.y);
        this.scale = Math.max(0.01, Math.min(16, this.scale * zoomFactor(e)));
        const after = this.toWorld(p.x, p.y);
        this.cx += before[0] - after[0]; this.cy += before[1] - after[1];
        this.invalidate();
      }, { passive: false });
      c.tabIndex = 0;
    }

    down(e) {
      const app = this.app;
      app.activeView = this;
      app.views.forEach((v) => v.invalidate && v.invalidate());
      try { this.canvas.focus({ preventScroll: true }); this.canvas.setPointerCapture(e.pointerId); } catch (err) { /* ignorieren */ }
      const p = this.pos(e);
      if (e.pointerType === 'touch') {
        this.touches.set(e.pointerId, p);
        if (this.touches.size === 2) {
          // Zwei Finger: schieben und zoomen, laufende Aktion verwerfen
          if (this.drag) { this.drag = null; app.endPreview(); }
          const [a, b] = Array.from(this.touches.values());
          this.gesture = { d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, scale0: this.scale, w0: this.toWorld((a.x + b.x) / 2, (a.y + b.y) / 2) };
          this.invalidate();
          return;
        }
        if (this.touches.size > 2) return;
      }
      if (e.button === 1 || e.button === 2 || (e.button === 0 && app.spaceDown)) {
        this.drag = { type: 'pan', x: p.x, y: p.y, cx: this.cx, cy: this.cy, id: e.pointerId, btn: e.button, moved: false };
        this.canvas.style.cursor = 'grabbing';
        return;
      }
      if (e.button !== 0) return;
      const w = this.toWorld(p.x, p.y);
      const tool = app.tool;
      if (tool === 'box') {
        const a = [this.snap(w[0]), this.snap(w[1])];
        this.drag = { type: 'box', a: a, b: a.slice(), id: e.pointerId };
      } else if (tool === 'entity') {
        const o = [0, 0, 0]; o[this.ax.h] = this.snap(w[0]); o[this.ax.v] = this.snap(w[1]); o[this.ax.d] = app.entDepth[this.ax.d];
        app.placeEntity(o);
      } else if (tool === 'clip') {
        const a = [this.snap(w[0]), this.snap(w[1])];
        this.drag = { type: 'clip', id: e.pointerId };
        app.clipLine = { view: this.key, a: a, b: a.slice() };
        app.onClipChange();
      } else if (tool === 'texture' && app.texMode === 'paint') {
        const hit = this.pickAll(p.x, p.y).find((c) => c.type === 'b');
        if (hit) { if (e.altKey) app.pickTexture(hit.id, null); else app.paintFace(hit.id, null); }
      } else if (tool === 'vertex') {
        const vh = this.hitVertex(p.x, p.y);
        if (vh) {
          // Doppelklick wählt alle in dieser Ansicht aufeinanderliegenden Punkte (wie in Hammer); sie bleiben gewählt und lassen sich gemeinsam ziehen
          const now = Date.now(), lc = this._vClick, dbl = lc && now - lc.t < 450 && Math.hypot(p.x - lc.x, p.y - lc.y) < 6;
          this._vClick = { t: now, x: p.x, y: p.y };
          let mode = e.altKey || e.shiftKey ? 'proj' : '3d', axes = this.ax, ref = vh.pos;
          if (dbl) { app.vertSel = { h: this.ax.h, v: this.ax.v, pt: [vh.pos[this.ax.h], vh.pos[this.ax.v]] }; mode = 'proj'; app.invalidateAll(); this._vClick = null; }
          else if (app.vertSel && Cmd.vertInSel(app.vertSel, vh.pos)) { const vs = app.vertSel; mode = 'proj'; axes = { h: vs.h, v: vs.v }; ref = [0, 0, 0]; ref[vs.h] = vs.pt[0]; ref[vs.v] = vs.pt[1]; }
          else if (app.vertSel) { app.vertSel = null; app.invalidateAll(); }
          const groups = Cmd.vertexGroups(app.doc, app.sel, ref, mode, axes);
          this.drag = { type: 'vertex', groups: groups, ref: vh.pos.slice(), doc0: app.doc, id: e.pointerId, moved: false };
          return;
        }
        this.downSelect(e, p, w);
      } else {
        this.downSelect(e, p, w);
      }
    }

    downSelect(e, p, w) {
      const app = this.app, tool = app.tool;
      const hi = tool === 'select' ? this.hitHandle(p.x, p.y) : -1;
      if (hi >= 0) {
        this.drag = { type: 'resize', handle: hi, box0: app.selBBox(), doc0: app.doc, id: e.pointerId, moved: false };
        return;
      }
      const all = this.pickAll(p.x, p.y);
      const sel = app.sel;
      const additive = e.shiftKey || e.ctrlKey || e.metaKey;
      // Bei Umschalt/Strg schaltet der oberste Treffer um; sonst bleibt ein schon gewähltes Element unter dem Zeiger das Ziel
      const hit = additive && !e.altKey ? all[0] || null : Cmd.pickHit(all, sel, e.altKey);
      const isSel = (c) => (c.type === 'b' ? sel.b.has(c.id) : sel.e.has(c.id));
      if (hit) {
        if (additive) {
          const ns = Cmd.cloneSel(sel);
          const set = hit.type === 'b' ? ns.b : ns.e;
          if (set.has(hit.id)) set.delete(hit.id); else set.add(hit.id);
          app.setSel(ns);
          return;
        }
        if (!isSel(hit)) {
          const ns = Cmd.emptySel();
          (hit.type === 'b' ? ns.b : ns.e).add(hit.id);
          app.setSel(ns);
        }
        if (tool === 'select') {
          this.drag = { type: 'move', start: w, doc0: app.doc, box0: app.selBBox(), id: e.pointerId, moved: false, px: p.x, py: p.y };
        }
        return;
      }
      this.drag = { type: 'marquee', a: p, b: p, add: e.shiftKey || e.ctrlKey || e.metaKey, id: e.pointerId };
    }

    move(e) {
      const app = this.app, p = this.pos(e), w = this.toWorld(p.x, p.y), d = this.drag, ax = this.ax;
      this.mouse = p;
      if (e.pointerType === 'touch' && this.touches.has(e.pointerId)) {
        this.touches.set(e.pointerId, p);
        if (this.gesture && this.touches.size === 2) {
          const [a, b] = Array.from(this.touches.values()), g = this.gesture;
          const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1, mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
          this.scale = Math.max(0.01, Math.min(16, g.scale0 * dist / g.d0));
          this.cx = g.w0[0] - (mx - this.w / 2) / this.scale; this.cy = g.w0[1] + (my - this.h / 2) / this.scale;
          this.invalidate();
          return;
        }
        if (this.gesture) return;
      }
      app.setCursor(this.key, w);
      if (!d) {
        // Hover-Rückmeldung
        let cur = 'default';
        if (app.tool === 'select') {
          const hi = this.hitHandle(p.x, p.y);
          if (hi >= 0) cur = HANDLE_CURSORS[hi];
          else if (app.spaceDown) cur = 'grab';
        } else if (app.tool === 'vertex') {
          const vh = this.hitVertex(p.x, p.y);
          cur = vh ? 'pointer' : 'default';
          const nh = vh ? vh.pos : null;
          if (nh !== this.hoverVertex) { this.hoverVertex = nh; this.invalidate(); if (nh) app.status(_('v2.vertex', { pos: nh.map((x) => Math.round(x * 100) / 100).join('  ') })); }
        } else cur = 'crosshair';
        this.canvas.style.cursor = cur;
        return;
      }
      if (d.id !== undefined && d.id !== e.pointerId) return;
      if (d.type === 'pan') {
        if (!d.moved && Math.hypot(p.x - d.x, p.y - d.y) > 3) d.moved = true;
        this.cx = d.cx - (p.x - d.x) / this.scale; this.cy = d.cy + (p.y - d.y) / this.scale;
        this.invalidate();
      } else if (d.type === 'marquee') {
        d.b = p; this.invalidate();
      } else if (d.type === 'box') {
        d.b = [this.snap(w[0]), this.snap(w[1])]; this.invalidate();
        // Vorschau auch in den anderen Ansichten (und in 3D): der Entwurf wird als Brush ins Vorschau-Dokument gelegt
        const bx = this.boxBounds(d);
        const nb = bx && Gens.shapeBrush(app.shape.kind, bx.mn, bx.mx, { sides: app.shape.sides, tex: app.defaultTex() });
        if (nb) app.preview(Cmd.addBrushList(app.doc, [nb]).doc); else app.endPreview();
      } else if (d.type === 'clip') {
        app.clipLine.b = [this.snap(w[0]), this.snap(w[1])]; app.onClipChange();
      } else if (d.type === 'move') {
        if (!d.moved && Math.hypot(p.x - d.px, p.y - d.py) < 3) return;
        d.moved = true;
        const g = app.snapOn ? app.grid : 1, bb = d.box0;
        const dh = Math.round((bb.min[ax.h] + (w[0] - d.start[0])) / g) * g - bb.min[ax.h];
        const dv = Math.round((bb.min[ax.v] + (w[1] - d.start[1])) / g) * g - bb.min[ax.v];
        const dd = [0, 0, 0]; dd[ax.h] = dh; dd[ax.v] = dv;
        app.preview(Cmd.move(d.doc0, app.sel, dd, app.lock));
        app.status(_('v2.moving', { d: dd.map((x) => Math.round(x * 100) / 100).join(', ') }));
      } else if (d.type === 'resize') {
        d.moved = true;
        const i = d.handle, nb = { min: d.box0.min.slice(), max: d.box0.max.slice() };
        const g = app.snapOn ? app.grid : 1, minSize = Math.max(1, g);
        const L = i === 0 || i === 6 || i === 7, R = i === 2 || i === 3 || i === 4, T = i === 0 || i === 1 || i === 2, B = i === 4 || i === 5 || i === 6;
        const sh = this.snap(w[0]), sv = this.snap(w[1]);
        if (L) nb.min[ax.h] = Math.min(sh, nb.max[ax.h] - minSize);
        if (R) nb.max[ax.h] = Math.max(sh, nb.min[ax.h] + minSize);
        if (B) nb.min[ax.v] = Math.min(sv, nb.max[ax.v] - minSize);
        if (T) nb.max[ax.v] = Math.max(sv, nb.min[ax.v] + minSize);
        if (e.shiftKey) { // Umschalt: Seitenverhältnis in dieser Ansicht erhalten; die Achse mit der größeren Änderung führt, die andere folgt, die Tiefe bleibt
          const b0 = d.box0, w0 = b0.max[ax.h] - b0.min[ax.h], h0 = b0.max[ax.v] - b0.min[ax.v];
          const wf = (nb.max[ax.h] - nb.min[ax.h]) / w0, vf = (nb.max[ax.v] - nb.min[ax.v]) / h0;
          let f = (L || R) && (T || B) ? (Math.abs(wf - 1) >= Math.abs(vf - 1) ? wf : vf) : (L || R) ? wf : vf;
          f = Math.max(f, minSize / Math.min(w0, h0));
          const fit = (k, lo0, hi0, atLo, atHi) => { // atLo/atHi: dieser Rand wird gezogen, der gegenüberliegende bleibt stehen
            const size = (hi0 - lo0) * f, lo = atLo ? hi0 - size : atHi ? lo0 : (lo0 + hi0) / 2 - size / 2;
            nb.min[k] = Math.round(lo); nb.max[k] = Math.max(Math.round(lo + size), nb.min[k] + 1);
          };
          fit(ax.h, b0.min[ax.h], b0.max[ax.h], L, R); fit(ax.v, b0.min[ax.v], b0.max[ax.v], B, T);
        }
        app.preview(Cmd.resize(d.doc0, app.sel, d.box0, nb));
      } else if (d.type === 'vertex') {
        d.moved = true;
        const delta = [0, 0, 0];
        delta[ax.h] = this.snap(w[0]) - d.ref[ax.h]; delta[ax.v] = this.snap(w[1]) - d.ref[ax.v];
        const nd = Cmd.moveVerts(d.doc0, d.groups, delta);
        if (nd) { app.preview(nd); d.ok = delta; } else { app.status(_('v2.move_invalid')); }
      }
    }

    up(e, cancel) {
      const app = this.app, d = this.drag;
      if (e.pointerType === 'touch') {
        this.touches.delete(e.pointerId);
        if (this.gesture) { if (this.touches.size < 2) this.gesture = this.touches.size ? this.gesture : null; return; }
      }
      if (!d || (d.id !== undefined && d.id !== e.pointerId)) return;
      this.drag = null;
      this.canvas.style.cursor = app.tool === 'select' ? 'default' : 'crosshair';
      const p = this.pos(e), ax = this.ax;
      if (cancel) { app.endPreview(); this.invalidate(); return; }
      if (d.type === 'pan' && d.btn === 2 && !d.moved) { this.invalidate(); app.contextAt(this, e); return; }
      if (d.type === 'marquee') {
        const a = this.toWorld(d.a.x, d.a.y), b = this.toWorld(d.b.x, d.b.y);
        if (Math.hypot(d.a.x - d.b.x, d.a.y - d.b.y) < 3) { if (!d.add) app.setSel(Cmd.emptySel()); this.invalidate(); return; }
        const lo = [Math.min(a[0], b[0]), Math.min(a[1], b[1])], hi = [Math.max(a[0], b[0]), Math.max(a[1], b[1])];
        const ns = d.add ? Cmd.cloneSel(app.sel) : Cmd.emptySel();
        app.doc.ents.forEach((en, i) => {
          en.brushes.forEach((br) => {
            const bb = info(br).bb;
            if (bb.min[ax.h] >= lo[0] && bb.max[ax.h] <= hi[0] && bb.min[ax.v] >= lo[1] && bb.max[ax.v] <= hi[1]) ns.b.add(br.id);
          });
          if (app.showEnts && Cmd.isPointEnt(en, i)) {
            const o = Core.parseVec(en.kv.origin);
            if (o && o[ax.h] >= lo[0] && o[ax.h] <= hi[0] && o[ax.v] >= lo[1] && o[ax.v] <= hi[1]) ns.e.add(en.id);
          }
        });
        app.setSel(ns);
      } else if (d.type === 'box') {
        const bx = this.boxBounds(d);
        if (bx) app.createShape(bx.mn, bx.mx); else app.endPreview();
        this.invalidate();
      } else if (d.type === 'clip') {
        const cl = app.clipLine;
        if (cl && Math.hypot(cl.a[0] - cl.b[0], cl.a[1] - cl.b[1]) < 1) app.clipLine = null;
        app.onClipChange();
      } else if (d.type === 'move' || d.type === 'resize') {
        if (d.moved && app.pv) app.commit(app.pv, d.type === 'move' ? _('cmd.move') : _('cmd.scale')); else app.endPreview();
      } else if (d.type === 'vertex') {
        if (d.moved && app.pv) {
          app.commit(app.pv, _('cmd.move_vertex'));
          const vs = app.vertSel; if (vs && d.ok) { vs.pt = [vs.pt[0] + d.ok[vs.h], vs.pt[1] + d.ok[vs.v]]; app.invalidateAll(); } // Auswahl wandert mit
        } else app.endPreview();
      }
      this.invalidate();
    }
  }
  View2D.AX = AX;
  return View2D;
})(Core, Cmd, EntDefs, Gens, Doors);
export default View2D;
